/** MaaS 创建流程共用载荷转换（组序列化、资源规范化、校验等） */
import { cloneDeep, get, set, unset } from 'lodash';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { commaTextToStartupArray, spaceTextToStartupArray } from '@/components-biz/ImageSelect';
import { placeRegistryOnGroupSystem } from '@/delivery-type-plugin/capabilities/image';
import type {
  MaasGroupSpec,
  MaasGroupedWorkloadSpec,
  MaasResource,
  MaasRuntimeType,
  MaasSharedFormValues,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  MaasFramework,
  MaasGroupRoleType,
  MaasInferenceSource,
  MaasRunMode,
  normalizeMaasRuntime,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { parseStorageQuantityToGi, toK8sGiQuantity } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import {
  finalizeGroupSpecForSubmit,
  findGroupVolume,
  getPrimaryGroupFromValues,
  getWorkerGroupListForSubmit,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import {
  hasDuplicateWorkerGroupNames,
  hasWorkerGroupReservedRoleName,
  isWorkerGroupNameDuplicated,
  isWorkerGroupReservedRoleName,
  validateNodeGroupResource,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupValidation';
import { parseInferenceFrameworkImageItem } from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';

const MAAS_GROUP_SYSTEM_TYPE = 'Container';

function normalizeArchitecture(arch?: unknown): string | undefined {
  const value = String(arch ?? '').trim();
  if (!value) return undefined;
  return value;
}

/** 保证 MaasResourceCPU 同时包含 cores 与 resource.cpu（Controller 依赖后者） */
function ensureMaasCpuResourceForApi(cpu?: MaasResource['cpu']): MaasResource['cpu'] | undefined {
  if (!cpu) return undefined;
  const next = cloneDeep(cpu);
  const coresQty = formatQuantityForApi(next.cores) ?? formatQuantityForApi(get(next, 'resource.cpu.request'));
  if (!coresQty) return next;

  const resourceMap = { ...(next.resource || {}) } as Record<string, { request?: string | number; limit?: string | number }>;
  const cpuEntry = resourceMap.cpu;
  if (!cpuEntry?.request) {
    resourceMap.cpu = {
      request: coresQty,
      limit: formatQuantityForApi(cpuEntry?.limit) ?? coresQty,
    };
    next.resource = resourceMap;
  }
  if (!next.cores) {
    next.cores = coresQty;
  }
  return next;
}

/** 从推理框架镜像读取最小系统盘大小（Gi） */
export function getMinSystemDiskSizeFromImage(item?: CommonImageItemType | null): number {
  const raw = item?.options?.systemImage?.systemVolumeSize;
  const size = Number(String(raw ?? '').replace('Gi', ''));
  return Number.isNaN(size) ? 10 : Math.max(size, 10);
}

/** 将推理框架 system 字段转为 API 可用的 system 对象 */
function buildInferenceSystemForApi(inferenceSystem?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!inferenceSystem) return undefined;
  const system = cloneDeep(inferenceSystem);
  if (typeof system.command === 'string') {
    const command = spaceTextToStartupArray(system.command);
    if (command.length) system.command = command;
    else delete system.command;
  }
  if (typeof system.args === 'string') {
    const args = commaTextToStartupArray(system.args);
    if (args.length) system.args = args;
    else delete system.args;
  }
  delete system.imageKey;
  return normalizeMaasGroupSystem(system);
}

function hasSystemStartupOverrides(system?: Record<string, unknown>): boolean {
  if (!system) return false;
  const command = system.command;
  const args = system.args;
  const hasCommand = Array.isArray(command) ? command.length > 0 : !!String(command ?? '').trim();
  const hasArgs = Array.isArray(args) ? args.length > 0 : !!String(args ?? '').trim();
  return hasCommand || hasArgs;
}

function hasSystemEnvOverrides(system?: Record<string, unknown>): boolean {
  if (!system) return false;
  const env = system.env;
  if (!Array.isArray(env)) return false;
  return env.some(
    (entry) => entry && String((entry as { name?: string; key?: string }).name ?? (entry as { key?: string }).key ?? '').trim(),
  );
}

/**
 * 合并节点组 system 与推理框架 system：镜像等以推理框架为准，启动命令/环境变量以组内用户编辑为准。
 */
export function mergeMaasGroupSystemForApi(
  groupSystem?: Record<string, unknown>,
  inferenceSystemRaw?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const builtInference = buildInferenceSystemForApi(inferenceSystemRaw);
  const builtGroup = buildInferenceSystemForApi(groupSystem);
  if (!builtInference) return builtGroup;
  if (!builtGroup) return builtInference;

  const startupOverridden = hasSystemStartupOverrides(groupSystem);
  const envOverridden = hasSystemEnvOverrides(groupSystem);
  const merged: Record<string, unknown> = { ...builtInference, ...builtGroup };

  if (!startupOverridden) {
    if (builtInference.command !== undefined) merged.command = builtInference.command;
    else delete merged.command;
    if (builtInference.args !== undefined) merged.args = builtInference.args;
    else delete merged.args;
  }
  if (!envOverridden) {
    if (builtInference.env !== undefined) merged.env = builtInference.env;
    else delete merged.env;
  }

  delete merged.imageKey;
  delete merged.imageName;
  return merged;
}

/** 将 CPU 核数等标量格式化为 K8s Quantity 字符串（与后端 resource.Quantity 一致） */
function formatQuantityForApi(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const raw = String(value).trim();
  if (/[a-zA-Z]/.test(raw)) return raw;
  const num = Number(raw);
  if (!Number.isNaN(num) && num > 0) return String(num);
  return raw;
}

/** 从 Maas CRD / 容器实例多种路径解析内存大小（Gi 数值，供表单 Slider 使用） */
function parseMemoryGiForForm(memory?: MaasResource['memory'] | Record<string, unknown>): number | undefined {
  if (!memory) return undefined;

  const candidates: unknown[] = [
    get(memory, 'request'),
    get(memory, 'limit'),
    get(memory, 'resource.memory.request'),
    get(memory, 'resource.memory.limit'),
  ];

  const resourceMap = memory.resource as Record<string, { request?: unknown; limit?: unknown }> | undefined;
  if (resourceMap && typeof resourceMap === 'object') {
    Object.values(resourceMap).forEach((entry) => {
      candidates.push(entry?.request, entry?.limit);
    });
  }

  for (const raw of candidates) {
    if (raw === undefined || raw === null || raw === '') continue;
    const gi = parseStorageQuantityToGi(raw);
    if (gi !== undefined && gi > 0) return gi;
  }
  return undefined;
}

/** 提交前规范化存储卷中的 storage quantity 字段 */
function normalizeMaasStorageVolumesForApi(storage?: MaasResource['storage']) {
  if (!storage?.volume?.length) return;

  storage.volume.forEach((volume) => {
    const quantityPaths = [
      'source.persistentVolumeClaimTemplate.resources.requests.storage',
      'source.persistentVolumeClaimTemplate.resources.limits.storage',
      'persistentVolumeClaimTemplate.resources.requests.storage',
      'persistentVolumeClaimTemplate.resources.limits.storage',
      'volumeTemplate.spec.pvc.resources.requests.storage',
      'volumeTemplate.spec.pvc.resources.limits.storage',
      'size.request',
      'size.limit',
    ];

    quantityPaths.forEach((path) => {
      const current = get(volume, path);
      if (current === undefined || current === null || current === '') return;
      const normalized = toK8sGiQuantity(current);
      if (normalized) {
        set(volume as object, path, normalized);
      } else {
        unset(volume, path);
      }
    });
  });
}

/** 判断是否为容器实例风格的 resource（cpu.request / memory.request） */
export function isContainerStyleResource(resource?: Record<string, unknown>): boolean {
  if (!resource) return false;
  return get(resource, 'cpu.request') !== undefined || get(resource, 'memory.request') !== undefined;
}

/** 容器实例 resource 格式 -> Maas CRD resource */
export function convertContainerResourceToMaas(resource?: Record<string, unknown>): MaasResource | undefined {
  if (!resource) return undefined;

  const result: MaasResource & { cpuPricing?: unknown; memoryPricing?: unknown } = {};
  const cpuRequest = get(resource, 'cpu.request');
  if (cpuRequest !== undefined && cpuRequest !== null && cpuRequest !== '') {
    const cores = formatQuantityForApi(cpuRequest);
    result.cpu = {
      cores,
      resource: {
        cpu: {
          request: cores ?? cpuRequest,
          limit: formatQuantityForApi(get(resource, 'cpu.limit', cpuRequest)) ?? cores ?? cpuRequest,
        },
      },
    } as MaasResource['cpu'];
  }

  const memRequest = get(resource, 'memory.request');
  if (memRequest !== undefined && memRequest !== null && memRequest !== '') {
    const memGi = toK8sGiQuantity(memRequest);
    result.memory = {
      resource: {
        memory: {
          request: memGi,
          limit: memGi,
        },
      },
    } as MaasResource['memory'];
  }

  const gpu = resource.gpu as Record<string, unknown> | undefined;
  /** 计价场景可能仅有 vendorID/deviceID；直建仍以 vendor 为主 */
  if (gpu && (gpu.vendor || gpu.vendorID || gpu.deviceID)) {
    const cardName = get(gpu, 'card.name');
    const cardRequest = get(gpu, 'card.request') ?? gpu.number;
    const driverVersion = String(gpu.driverVersion ?? '').trim();
    const cudaVersion = String(gpu.cudaVersion ?? '').trim();
    const gpuResource =
      cardName && cardRequest !== undefined && cardRequest !== null && cardRequest !== ''
        ? { [String(cardName)]: { request: cardRequest, limit: cardRequest } }
        : undefined;
    // 后端 MaaSResourceGPU 通过 software[] 持久化驱动/CUDA 版本，这里显式落到 software。
    const gpuSoftware =
      driverVersion || cudaVersion
        ? [
            {
              ...(driverVersion ? { driverVersion } : {}),
              ...(cudaVersion ? { cudaVersion } : {}),
            },
          ]
        : undefined;
    result.gpu = {
      resourceType: String(gpu.resourceType ?? '').trim() || undefined,
      vendor: gpu.vendor as string,
      device: gpu.device as string,
      deviceType: gpu.deviceType as string,
      vendorID: gpu.vendorID as string,
      deviceID: gpu.deviceID as string,
      ...(gpu.memory !== undefined && gpu.memory !== null && gpu.memory !== '' ? { memory: gpu.memory as string | number } : {}),
      // 计价场景常仅有 number、无 card.name；保留平铺 number 供 toForm / 询价往返
      ...(cardRequest !== undefined && cardRequest !== null && cardRequest !== '' ? { number: cardRequest } : {}),
      ...(gpu.quantityUnit != null && String(gpu.quantityUnit).trim() !== '' ? { quantityUnit: String(gpu.quantityUnit).trim() } : {}),
      // 兼容旧结构：保留平铺字段，便于历史数据链路继续工作。
      ...(driverVersion ? { driverVersion } : {}),
      ...(cudaVersion ? { cudaVersion } : {}),
      ...(gpuSoftware ? { software: gpuSoftware } : {}),
      resource: gpuResource,
    } as MaasResource['gpu'];
  }

  if (resource.storage) {
    result.storage = cloneDeep(resource.storage) as MaasResource['storage'];
  }
  const embeddedNics = resource.nics ?? get(resource, 'network.nics');
  if (embeddedNics) {
    result.nics = cloneDeep(embeddedNics) as MaasResource['nics'];
  }
  if (resource.network) {
    const network = cloneDeep(resource.network) as Record<string, unknown>;
    delete network.nics;
    if (Object.keys(network).length) {
      result.network = network as MaasResource['network'];
    }
  }

  /**
   * SKU/下单询价依赖 cpuPricing/memoryPricing（vendorID/deviceID）。
   * buildSkuResourceConfigs 会 form→spec→toForm 往返，必须保留，否则换厂商 selectedResources 不变被去重跳过。
   */
  if (resource.cpuPricing) {
    result.cpuPricing = cloneDeep(resource.cpuPricing);
  }
  if (resource.memoryPricing) {
    result.memoryPricing = cloneDeep(resource.memoryPricing);
  }

  return Object.keys(result).length ? result : undefined;
}

/** Maas CRD resource -> 容器实例 resource 格式（编辑回显） */
export function convertMaasResourceToContainer(resource?: MaasResource): Record<string, unknown> | undefined {
  if (!resource) return undefined;

  const result: Record<string, unknown> = {};
  const cpuCores = resource.cpu?.cores ?? get(resource.cpu, 'resource.cpu.request');
  if (cpuCores !== undefined && cpuCores !== null && cpuCores !== '') {
    const request = Number(String(cpuCores).replace(/[^0-9.]/g, '')) || cpuCores;
    result.cpu = { request, limit: request };
  }

  const memoryRaw = resource.memory;
  const memGi = parseMemoryGiForForm(memoryRaw);
  if (memGi !== undefined) {
    result.memory = {
      vendor: memoryRaw?.vendor,
      device: memoryRaw?.device,
      deviceType: memoryRaw?.deviceType,
      vendorID: memoryRaw?.vendorID,
      deviceID: memoryRaw?.deviceID,
      request: memGi,
      limit: memGi,
    };
  }

  const gpu = resource.gpu;
  if (gpu && (gpu.vendor || gpu.vendorID || gpu.deviceID)) {
    const resourceMap = gpu.resource as Record<string, { request?: unknown }> | undefined;
    const cardName = resourceMap ? Object.keys(resourceMap)[0] : undefined;
    const cardRequest = cardName ? resourceMap?.[cardName]?.request : undefined;
    // 兼容历史/后端差异：驱动与 CUDA 版本可能落在 software 列表或其它字段。
    const softwareList = get(gpu, 'software') as Record<string, unknown>[] | undefined;
    const firstSoftware = Array.isArray(softwareList) ? softwareList.find((item) => item && typeof item === 'object') : undefined;
    const resolvedDriverVersion =
      (gpu.driverVersion as string | undefined) ||
      (get(gpu, 'driver.version') as string | undefined) ||
      (get(gpu, 'driver') as string | undefined) ||
      (firstSoftware?.driverVersion as string | undefined) ||
      (firstSoftware?.driver as string | undefined);
    const resolvedCudaVersion =
      (gpu.cudaVersion as string | undefined) ||
      (get(gpu, 'cuda.version') as string | undefined) ||
      (get(gpu, 'softwareStack.version') as string | undefined) ||
      (firstSoftware?.cudaVersion as string | undefined);
    result.gpu = {
      resourceType: gpu.resourceType as string | undefined,
      vendor: gpu.vendor,
      device: gpu.device,
      deviceType: gpu.deviceType,
      vendorID: gpu.vendorID,
      deviceID: gpu.deviceID,
      memory: gpu.memory,
      driverVersion: resolvedDriverVersion,
      cudaVersion: resolvedCudaVersion,
      number: cardRequest ?? (gpu as { number?: unknown }).number,
      card: cardName ? { name: cardName, request: cardRequest } : undefined,
      code: (get(gpu, 'type') as string | undefined) || (get(gpu, 'code') as string | undefined),
      ...((gpu as { quantityUnit?: unknown }).quantityUnit != null && String((gpu as { quantityUnit?: unknown }).quantityUnit).trim() !== ''
        ? { quantityUnit: String((gpu as { quantityUnit?: unknown }).quantityUnit).trim() }
        : {}),
    };
  }

  if (resource.storage) {
    result.storage = cloneDeep(resource.storage);
  }
  if (resource.nics) {
    result.nics = cloneDeep(resource.nics);
  }
  if (resource.network) {
    result.network = cloneDeep(resource.network);
  }

  const raw = resource as MaasResource & { cpuPricing?: unknown; memoryPricing?: unknown };
  if (raw.cpuPricing) {
    result.cpuPricing = cloneDeep(raw.cpuPricing);
  }
  if (raw.memoryPricing) {
    result.memoryPricing = cloneDeep(raw.memoryPricing);
  }

  return Object.keys(result).length ? result : undefined;
}

/** 组 spec 内 resource 转为容器实例表单格式 */
export function convertMaasGroupResourceForForm(group?: MaasGroupSpec): MaasGroupSpec | undefined {
  if (!group?.resource) return group;
  const containerResource = convertMaasResourceToContainer(group.resource as MaasResource);
  if (!containerResource) return group;
  return {
    ...group,
    resource: containerResource as unknown as MaasResource,
  };
}

/** 提交前规范化组级 CPU / 内存 / GPU 资源字段 */
export function normalizeMaasGroupResourceForApi(resource?: MaasResource | Record<string, unknown>): MaasResource | undefined {
  if (!resource) return undefined;

  const raw = resource as Record<string, unknown>;
  const next: MaasResource = isContainerStyleResource(raw)
    ? convertContainerResourceToMaas(raw) || {}
    : cloneDeep(resource as MaasResource);

  const memReq = get(next, 'memory.resource.memory.request');
  const memGi = toK8sGiQuantity(memReq);
  if (memGi) {
    set(next, 'memory.resource.memory.request', memGi);
    set(next, 'memory.resource.memory.limit', memGi);
  }

  const gpuResource = get(next, 'gpu.resource') as Record<string, { request?: unknown; limit?: unknown }> | undefined;
  if (gpuResource && typeof gpuResource === 'object') {
    Object.keys(gpuResource).forEach((key) => {
      const entry = gpuResource[key];
      if (!entry || entry.request === undefined || entry.request === null || entry.request === '') return;
      if (entry.limit === undefined || entry.limit === null || entry.limit === '') {
        entry.limit = entry.request;
      }
    });
  }

  const hasGpuResource =
    gpuResource &&
    typeof gpuResource === 'object' &&
    Object.values(gpuResource).some((entry) => entry?.request !== undefined && entry?.request !== null && entry?.request !== '');

  if (!next.gpu?.vendor && !hasGpuResource) {
    delete next.gpu;
  }

  if (next.cpu) {
    next.cpu = ensureMaasCpuResourceForApi(next.cpu);
  }

  if (next.storage) {
    normalizeMaasStorageVolumesForApi(next.storage);
  }

  return Object.keys(next).length ? next : undefined;
}

/** 自定义镜像 system 仅保留运行时必要字段，避免残留 catalog 元数据导致编辑回填误判为内置 */
export function stripCatalogInferenceMetadataFromSystem(system?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!system) return system;
  const imageUrl = String(system.image ?? system.url ?? '').trim();
  if (!imageUrl) return undefined;

  const next: Record<string, unknown> = {
    image: imageUrl,
    url: imageUrl,
  };
  for (const key of ['type', 'command', 'args', 'env', 'livenessProbe', 'devices', 'security', 'architecture'] as const) {
    if (system[key] !== undefined) {
      next[key] = system[key];
    }
  }
  return next;
}

/**
 * 推断推理框架来源。
 * 提交时 imageKey / imageName 会被剔除，自定义镜像也会写入 image/url，不能仅用 URL 判断。
 * 持久化 system 无 catalog 元数据（os/version/imageKey）时视为自定义；编辑回填可结合 maasForm.inferenceFrameworkImage。
 */
export function resolveInferenceSourceFromSystem(system?: Record<string, unknown>): MaasInferenceSource {
  if (!system) return MaasInferenceSource.Builtin;
  if (system.imageKey) return MaasInferenceSource.Builtin;
  const imageUrl = system.image || system.url;
  if (!imageUrl) return MaasInferenceSource.Builtin;
  const hasCatalogMetadata = !!(system.os || system.version);
  return hasCatalogMetadata ? MaasInferenceSource.Builtin : MaasInferenceSource.Custom;
}

function readPersistedSystemImageUrl(system?: Record<string, unknown>): string {
  return String(system?.image ?? system?.url ?? '').trim();
}

function readCatalogImageUrl(catalogItem: CommonImageItemType): string {
  return String(catalogItem.dockerRefUrl ?? catalogItem.dockerSourceUrl ?? catalogItem.name ?? '').trim();
}

/** system 持久化 URL 是否与 catalog 镜像项一致（用于识别过期 annotation） */
export function isPersistedSystemImageMatchingCatalog(
  system: Record<string, unknown> | undefined,
  catalogItem: CommonImageItemType | null,
): boolean {
  if (!catalogItem) return false;
  const systemUrl = readPersistedSystemImageUrl(system);
  const catalogUrl = readCatalogImageUrl(catalogItem);
  if (!systemUrl || !catalogUrl) return false;
  return systemUrl === catalogUrl;
}

/** 是否为可信的 catalog 快照（区别于仅用 URL 构造的展示用镜像项） */
function isAuthoritativeCatalogSnapshot(catalogItem: CommonImageItemType, system?: Record<string, unknown>): boolean {
  const key = String(catalogItem.key ?? catalogItem.id ?? '').trim();
  const catalogUrl = readCatalogImageUrl(catalogItem);
  if (!key || !catalogUrl) return false;
  if (String(catalogItem.artifactType ?? '').trim()) return true;
  if (system?.imageKey || system?.os || system?.version) return true;
  // buildImageItemFromInferenceSystem 在 system 仅有 URL 时会用 URL 作为 key
  if (key === catalogUrl) return false;
  return true;
}

/**
 * 编辑/详情回填镜像来源：
 * - 内置镜像：maasForm 含 inferenceFrameworkImage（API system 仅留 URL，无法单独区分）
 * - 自定义镜像：保存时不写 inferenceFrameworkImage；若 annotation 过期则对比 system URL 与 catalog URL
 */
export function resolveMaasInferenceSourceForForm(values: {
  inferenceFrameworkImage?: MaasSharedFormValues['inferenceFrameworkImage'];
  _inferenceSystem?: MaasSharedFormValues['_inferenceSystem'];
  maasInferenceSource?: MaasInferenceSource;
}): MaasInferenceSource {
  if (values.maasInferenceSource === MaasInferenceSource.Custom) {
    return MaasInferenceSource.Custom;
  }
  if (values.maasInferenceSource === MaasInferenceSource.Builtin) {
    return MaasInferenceSource.Builtin;
  }

  const system = parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
  const systemResolved = resolveInferenceSourceFromSystem(system);
  const catalogItem = parseInferenceFrameworkImageItem(values.inferenceFrameworkImage);

  if (catalogItem && isAuthoritativeCatalogSnapshot(catalogItem, system)) {
    if (isPersistedSystemImageMatchingCatalog(system, catalogItem)) {
      return MaasInferenceSource.Builtin;
    }
    const systemUrl = readPersistedSystemImageUrl(system);
    if (systemUrl) return MaasInferenceSource.Custom;
  }

  return systemResolved;
}

/** 从推理框架镜像解析 framework 字段 */
export function resolveFrameworkFromInferenceImage(
  item?: CommonImageItemType | null,
  masterSystem?: Record<string, unknown>,
): MaasFramework {
  const fromForm = masterSystem?.framework as MaasFramework | undefined;
  if (fromForm === MaasFramework.VLLM || fromForm === MaasFramework.SGlang) return fromForm;

  const fromOptions = item?.options?.framework || item?.options?.baseFramework;
  if (fromOptions === MaasFramework.SGlang || fromOptions === 'SGlang') return MaasFramework.SGlang;
  if (fromOptions === MaasFramework.VLLM || fromOptions === 'vLLM') return MaasFramework.VLLM;

  const name = String(item?.name || masterSystem?.imageName || '').toLowerCase();
  if (name.includes('sglang')) return MaasFramework.SGlang;
  return MaasFramework.VLLM;
}

/** 提交前从主组镜像重新解析 framework（自定义镜像不依赖 onBlur） */
export function ensureFrameworkForSubmit<
  T extends {
    runMode?: MaasRunMode;
    framework?: MaasFramework;
    headGroupSpec?: MaasGroupSpec;
    workerGroupSpecs?: MaasGroupSpec[];
    masterNodes?: string;
    workerNodes?: string;
    /** 与 MaasSharedFormValues 一致，hidden 字段可为 JSON 字符串 */
    _inferenceSystem?: Record<string, unknown> | string;
  },
>(values: T): T {
  const primary = getPrimaryGroupFromValues(values as MaasSharedFormValues, values.runMode);
  const system = primary?.system as Record<string, unknown> | undefined;
  const framework = resolveFrameworkFromInferenceImage(null, { ...system, framework: values.framework });
  return { ...values, framework };
}

/** 补全 system 镜像相关字段，对齐 ResourceSystemInfomation */
export function normalizeMaasGroupSystem(system?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!system) return undefined;
  const image = String(system.image || system.url || '');
  const normalizedArch = normalizeArchitecture(system.architecture);
  const next: Record<string, unknown> = {
    ...system,
    type: system.type || MAAS_GROUP_SYSTEM_TYPE,
    os: system.os ?? undefined,
    version: system.version ?? undefined,
    bootloader: system.bootloader || undefined,
    url: system.url || image,
    ...(image ? { image } : {}),
  };
  if (normalizedArch) {
    next.architecture = normalizedArch;
  } else {
    delete next.architecture;
  }
  return next;
}

/** 补全组级 replicas 与 system，满足集群 CRD 必填约束 */
export function normalizeMaasGroupSpec(group: MaasGroupSpec, role: MaasGroupRoleType, runtimeType?: MaasRuntimeType): MaasGroupSpec {
  const next = cloneDeep(group);
  if (!next.replicas) {
    next.replicas = 1;
  }
  if (next.system) {
    next.system = normalizeMaasGroupSystem(next.system);
    const system = next.system as Record<string, unknown>;
    if (typeof system.command === 'string') {
      const command = spaceTextToStartupArray(system.command);
      if (command.length) system.command = command;
      else delete system.command;
    }
    if (typeof system.args === 'string') {
      const args = commaTextToStartupArray(system.args);
      if (args.length) system.args = args;
      else delete system.args;
    }
  }
  return next;
}

/** 提交 API 时剔除仅用于前端的 system 字段 */
function sanitizeMaasGroupSystemForApi(system?: Record<string, unknown>): Record<string, unknown> | undefined {
  const normalized = normalizeMaasGroupSystem(system);
  if (!normalized) return undefined;
  const next = { ...normalized };
  delete next.imageKey;
  delete next.imageName;
  delete next.minSystemDiskGi;
  return next;
}

/** 提交 API 时保留 GPU resourceType（编辑回填依赖持久化字段） */
function normalizeGpuResourceForApi(resource?: MaasResource): MaasResource | undefined {
  return resource;
}

/** 序列化为 API 请求体中的组对象（对齐 MaasGroupSpec CRD，不含 group 级 type） */
export function serializeMaasGroupForApi(
  group: MaasGroupSpec,
  role: MaasGroupRoleType,
  runtimeType?: MaasRuntimeType,
): Record<string, unknown> {
  const normalized = normalizeMaasGroupSpec(placeRegistryOnGroupSystem(group), role, runtimeType);
  const resource = normalizeGpuResourceForApi(normalizeMaasGroupResourceForApi(normalized.resource)) as
    | (MaasResource & { cpuPricing?: unknown; memoryPricing?: unknown })
    | undefined;
  /** 询价用元数据不进入 CRD 请求体 */
  if (resource) {
    delete resource.cpuPricing;
    delete resource.memoryPricing;
    const gpu = resource.gpu as Record<string, unknown> | undefined;
    if (gpu && typeof gpu === 'object') {
      const { quantityUnit: _quantityUnit, ...gpuWithoutBillingUnit } = gpu;
      resource.gpu = gpuWithoutBillingUnit as MaasResource['gpu'];
    }
  }
  const body: Record<string, unknown> = {
    name: normalized.name,
    replicas: normalized.replicas ?? 1,
    system: sanitizeMaasGroupSystemForApi(normalized.system as Record<string, unknown> | undefined),
    resource,
  };
  if (normalized.initCommand?.commands?.length) {
    body.initCommand = normalized.initCommand;
  }
  return body;
}

/** 序列化 MaaS workload spec 为 API 请求体（剥离组内 UI 字段） */
export function buildMaasWorkloadRequestBody(spec: MaasGroupedWorkloadSpec): Record<string, unknown> {
  const runtimeType = spec.runtime?.type;
  const workerGroupSpecs = (spec.workerGroupSpecs ?? []).map((group) =>
    serializeMaasGroupForApi(group, MaasGroupRoleType.Worker, runtimeType),
  );

  const body: Record<string, unknown> = {
    alias: spec.alias,
    description: spec.description ?? '',
    workspace: spec.workspace,
    creator: spec.creator,
    mode: spec.mode,
    framework: spec.framework,
    runMode: spec.runMode,
    runtime: normalizeMaasRuntime(spec.runtime),
    network: spec.network,
    workerGroupSpecs,
  };

  if (typeof spec.replicas === 'number' && spec.replicas >= 0) {
    body.replicas = spec.replicas;
  }

  if (spec.environment?.env?.length) {
    body.environment = { env: spec.environment.env };
  }

  if (spec.annotations && Object.keys(spec.annotations).length) {
    body.annotations = spec.annotations;
  }

  if (spec.headGroupSpec) {
    body.headGroupSpec = serializeMaasGroupForApi(spec.headGroupSpec, MaasGroupRoleType.Master, runtimeType);
  }

  if (spec.gateway) {
    body.gateway = serializeMaasGroupForApi(spec.gateway, MaasGroupRoleType.Worker, runtimeType);
  }

  if (spec.initCommand?.commands?.length) {
    body.initCommand = spec.initCommand;
  }

  if (spec.taskTemplate?.length) {
    body.taskTemplate = spec.taskTemplate;
  }

  return body;
}

function normalizeGroupForSubmit(
  group: MaasGroupSpec,
  role: MaasGroupRoleType,
  runtimeType?: MaasRuntimeType,
  mountPath?: string,
  dataVolumeName?: string,
): MaasGroupSpec {
  let next = normalizeMaasGroupSpec(group, role, runtimeType);
  if (mountPath) {
    next = finalizeGroupSpecForSubmit(next, mountPath, { dataVolumeName });
  }
  if (next.resource) {
    next.resource = normalizeMaasGroupResourceForApi(next.resource) || next.resource;
  }
  return next;
}

/** 是否含推理镜像或数据盘（用于识别误写在 worker 列表中的主组配置） */
function groupHasPrimaryGroupPayload(group?: MaasGroupSpec): boolean {
  if (!group) return false;
  const volumes = get(group, 'resource.storage.volume') as Record<string, unknown>[] | undefined;
  if (findGroupVolume(volumes, 'Data')) return true;
  const system = group.system as Record<string, unknown> | undefined;
  return !!(system?.image || system?.imageKey || system?.url);
}

/**
 * 按节点角色拆分提交字段：Master -> headGroupSpec，Worker -> workerGroupSpecs
 */
export function resolveGroupSpecsForSubmit(
  values: MaasSharedFormValues,
  runtimeType?: MaasRuntimeType,
  mountPath?: string,
  options?: { dataVolumeName?: string; embeddedNodeConfig?: boolean },
): { headGroupSpec?: MaasGroupSpec; workerGroupSpecs: MaasGroupSpec[] } {
  const perNodeSystem = options?.embeddedNodeConfig === true;
  const dataVolumeName = options?.dataVolumeName;
  const runMode = values.runMode || MaasRunMode.Normal;
  let headGroupSpec = values.headGroupSpec ? cloneDeep(values.headGroupSpec) : undefined;
  let workerGroupSpecs = getWorkerGroupListForSubmit(values);

  if (runMode === MaasRunMode.Single) {
    const primary = workerGroupSpecs[0];
    return {
      headGroupSpec: undefined,
      workerGroupSpecs: primary
        ? [
            normalizeGroupForSubmit(
              { ...primary, name: primary.name || 'worker', replicas: 1 },
              MaasGroupRoleType.Worker,
              runtimeType,
              mountPath,
              dataVolumeName,
            ),
          ]
        : [],
    };
  }

  if (runMode === MaasRunMode.Distributed || runMode === MaasRunMode.Normal) {
    const primarySystem = workerGroupSpecs[0]?.system as Record<string, unknown> | undefined;
    workerGroupSpecs = workerGroupSpecs.map((group, index) => {
      const normalized = normalizeGroupForSubmit(
        { ...group, name: group.name || 'worker' },
        MaasGroupRoleType.Worker,
        runtimeType,
        mountPath,
        dataVolumeName,
      );
      if (!perNodeSystem && index > 0 && primarySystem) {
        const mergedSystem = mergeMaasGroupSystemForApi(normalized.system as Record<string, unknown> | undefined, primarySystem);
        if (mergedSystem) normalized.system = mergedSystem;
      }
      return normalized;
    });
    return { headGroupSpec: undefined, workerGroupSpecs };
  }

  // 主从模式：主组（推理、存储等）在 headGroupSpec
  if (!headGroupSpec && workerGroupSpecs.length && groupHasPrimaryGroupPayload(workerGroupSpecs[0])) {
    headGroupSpec = workerGroupSpecs[0];
    workerGroupSpecs = workerGroupSpecs.slice(1);
  }

  workerGroupSpecs = workerGroupSpecs.filter((g) => !isWorkerGroupReservedRoleName(g.name));

  const masterSystem = headGroupSpec?.system as Record<string, unknown> | undefined;
  if (headGroupSpec) {
    headGroupSpec = normalizeGroupForSubmit(
      { ...headGroupSpec, name: headGroupSpec.name || 'master', replicas: headGroupSpec.replicas || 1 },
      MaasGroupRoleType.Master,
      runtimeType,
      mountPath,
      dataVolumeName,
    );
  }

  workerGroupSpecs = workerGroupSpecs.map((group) => {
    const normalized = normalizeGroupForSubmit(
      { ...group, name: group.name || 'worker' },
      MaasGroupRoleType.Worker,
      runtimeType,
      mountPath,
      dataVolumeName,
    );
    if (!perNodeSystem) {
      const mergedSystem = mergeMaasGroupSystemForApi(normalized.system as Record<string, unknown> | undefined, masterSystem);
      if (mergedSystem) normalized.system = mergedSystem;
    }
    return normalized;
  });

  return { headGroupSpec, workerGroupSpecs };
}
